// components/TokenChecker.jsx
import React, {useCallback, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {syncAuthState, tokenExpired, validateToken} from '@/store/features/authSlice';
import TokenService from '@/services/TokenService';

const TokenChecker = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { isAuthenticated, tokenExpired: isTokenExpired } = useSelector(state => state.auth);

    const checkToken = useCallback(() => {
        const token = TokenService.getToken();
        const currentPath = location.pathname;

        // Si sur une route publique, pas besoin de vérifier
        if (isPublicRoute(currentPath)) {
            return true;
        }

        // Pas de token ou token invalide
        if (!token || !TokenService.isValidToken(token)) {
            if (token) {
                // Token existe mais est invalide
                dispatch(tokenExpired());
            }

            if (!isPublicRoute(currentPath)) {
                navigate('/login', {
                    state: {
                        from: currentPath,
                        reason: token ? 'token_expired' : 'no_token'
                    },
                    replace: true
                });
            }
            return false;
        }

        // Token valide mais expire bientôt (5 minutes)
        if (TokenService.willExpireSoon(5, token)) {
            handleTokenExpiringSoon(token);
        }

        // Mettre à jour l'état Redux
        dispatch(validateToken());
        return true;
    }, [dispatch, navigate, location.pathname]);

    const handleTokenExpiringSoon = (token) => {
        const timeUntilExpiration = TokenService.getTimeUntilExpiration(token);
        const minutes = Math.floor(timeUntilExpiration / 60000);

        if (minutes <= 5) {
            // Émettre un événement personnalisé pour les notifications
            window.dispatchEvent(new CustomEvent('tokenWillExpireSoon', {
                detail: {
                    minutes,
                    timeUntilExpiration,
                    expiresAt: TokenService.getTokenExpiration(token)
                }
            }));

            // Notification console pour le développement
            console.warn(`⚠️ Token expirant dans ${minutes} minute(s)`);
        }
    };

    const isPublicRoute = (pathname) => {
        const publicRoutes = [
            '/login',
            '/register',
            '/forgot-password',
            '/reset-password',
            '/unauthorized',
            '/public'
        ];
        return publicRoutes.some(route => pathname.startsWith(route));
    };

    useEffect(() => {
        // Vérifier immédiatement
        checkToken();

        // Configurer l'intervalle de vérification (toutes les minutes)
        const intervalId = setInterval(checkToken, 60000);

        // Vérifier quand la fenêtre redevient active
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkToken();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Vérifier quand l'utilisateur interagit avec la page
        const handleUserInteraction = () => {
            checkToken();
        };

        // Événements d'interaction utilisateur
        const events = ['click', 'keypress', 'scroll', 'mousemove'];
        events.forEach(event => {
            window.addEventListener(event, handleUserInteraction, { passive: true });
        });

        // Nettoyage
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            events.forEach(event => {
                window.removeEventListener(event, handleUserInteraction);
            });
        };
    }, [checkToken]);

    // Effet pour rediriger si token expiré
    useEffect(() => {
        if (isTokenExpired && !isPublicRoute(location.pathname)) {
            navigate('/login', {
                state: {
                    from: location.pathname,
                    reason: 'token_expired'
                },
                replace: true
            });
        }
    }, [isTokenExpired, location.pathname, navigate]);

    // Synchroniser l'état au chargement
    useEffect(() => {
        dispatch(syncAuthState());
    }, [dispatch]);

    return <>{children}</>;
};

export default TokenChecker;